// The "About the company" block's pure parts: which text to show, its hint, and the chat
// prompt that refreshes Scout's saved research. Kept apart from the component so `node --test`
// covers them without a DOM.
import type { BookAccount, CompanyResearch } from '../api/types';
import { formatShortDay } from '../lib/format.ts';

export interface OverviewSource {
  text: string;
  hint: string | null;
  // Scout's saved research, when that is what the block shows; null for a mirror overview.
  research: CompanyResearch | null;
}

/** "Saved 2 Oct 2026": when the record was saved, and nothing about who saved it. */
export function researchHint(research: CompanyResearch): string {
  const day = research.researched_at ? formatShortDay(research.researched_at) : null;
  return day ? `Saved ${day}` : 'Saved';
}

/** Saved research first; the Salesforce mirror's summary or description when there is none. */
export function overviewSource(row: BookAccount): OverviewSource | null {
  const research = row.company_research;
  if (research?.summary) {
    return { text: research.summary, hint: researchHint(research), research };
  }
  if (row.business_summary) {
    return { text: row.business_summary, hint: row.triaged_on ? `triaged ${row.triaged_on}` : null, research: null };
  }
  if (row.description) {
    return { text: row.description, hint: 'from Salesforce', research: null };
  }
  return null;
}

export interface ResearchDetailLine {
  label: string;
  text: string;
}

/** The saved research's labelled lines for the Company tab: "Where voice fits" first (the
 * record's voice angle, otherwise only on the Research tab), then each detail with text, once
 * per label. Empty for no research. */
export function researchDetailLines(research: CompanyResearch | null | undefined): ResearchDetailLine[] {
  if (!research) return [];
  const lines: ResearchDetailLine[] = [];
  const voiceAngle = research.voice_angle?.trim();
  if (voiceAngle) lines.push({ label: 'Where voice fits', text: voiceAngle });

  // Each labelled line once; blanks dropped.
  for (const detail of research.details ?? []) {
    const label = detail.label?.trim();
    const text = detail.text?.trim();
    if (!label || !text || lines.some(line => line.label === label)) continue;
    lines.push({ label, text });
  }
  return lines;
}

/** The message a Refresh click puts in the account's chat: Scout reads the saved record first. */
export function refreshPrompt(row: BookAccount): string {
  const company = row.domain || row.account;
  const research = row.company_research;
  if (research?.researched_at) {
    return `Refresh your research on ${company}: what is new since ${formatShortDay(research.researched_at)}?`;
  }
  return `Research ${company}: what they do, what is new, and where voice fits.`;
}

/** "acme.com/blog" for a source link's text; the full URL when it does not parse. */
export function sourceHost(url: string): string {
  try {
    const parsed = new URL(url);
    return parsed.host.replace(/^www\./u, '');
  } catch {
    return url;
  }
}

// A bare "host.tld/path" with no scheme, as the sweep and models sometimes write a source. It
// becomes an https link; anything with a scheme of its own goes through the protocol check.
const SCHEMELESS_URL = /^(?:[a-z0-9-]+\.)+[a-z]{2,}(?:[/?#][^\s]*)?$/i;

/** The URL when it is an absolute http(s) link (or a bare host and path, read as https), else
 * null: a model-written `javascript:` or `data:` link must never become an href in the panel. */
export function safeHref(url: string | null | undefined): string | null {
  if (!url) return null;
  const candidate = SCHEMELESS_URL.test(url) ? 'https://' + url : url;
  try {
    const parsed = new URL(candidate);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.href : null;
  } catch {
    return null;
  }
}

/** One piece of a text: plain words, or an http(s) link and the href it opens. */
export interface TextSegment {
  text: string;
  href: string | null;
}

// An http(s) link inside prose; sentence punctuation after it is not part of it.
const URL_IN_TEXT = /https?:\/\/[^\s<>"'|]+/g;
const URL_TRAILING_PUNCTUATION = /[.,;:!?)\]}]+$/;

/** The text split into plain and linked pieces, every http(s) URL in it a link through
 * `safeHref`. `clippedAt`: the text was cut at this many characters, so a link that runs to its
 * very end is cut too and stays plain text rather than opening a broken page. */
export function linkSegments(text: string, clippedAt?: number): TextSegment[] {
  const segments: TextSegment[] = [];
  const clipped = clippedAt != null && text.length >= clippedAt;
  let position = 0;
  for (const match of text.matchAll(URL_IN_TEXT)) {
    const url = match[0].replace(URL_TRAILING_PUNCTUATION, '');
    const start = match.index ?? 0;
    const end = start + url.length;
    const href = clipped && end >= text.length ? null : safeHref(url);
    if (!href) continue;
    if (start > position) segments.push({ text: text.slice(position, start), href: null });
    segments.push({ text: url, href });
    position = end;
  }
  if (position < text.length) segments.push({ text: text.slice(position), href: null });
  return segments;
}

/** The text's first http(s) link through `safeHref`, or null. */
export function firstLinkIn(text: string | null | undefined): string | null {
  if (!text) return null;
  return linkSegments(text).find(segment => segment.href)?.href ?? null;
}
